/* Landing page copy — English and Egyptian colloquial Arabic (عامي مصري).
   Every array below is positional: index 0 in `en` describes the same
   thing as index 0 in `ar`. LandingPage.jsx zips these with icon/image
   arrays that don't change between languages. */

export const translations = {
  en: {
    dir: "ltr",
    skipToContent: "Skip to content",
    requestDemo: "Request a Demo",
    signIn: "Sign In",
    openDashboard: "Open Dashboard",
    callUs: "Chat on WhatsApp",
    phoneDisplay: "01010706163",
    menuOpen: "Open menu",
    menuClose: "Close menu",
    brandHome: "Nexploy — Home",
    langSwitchLabel: "Language",

    nav: {
      platform: "Platform",
      attendance: "Attendance",
      payroll: "Payroll",
      mobile: "Web & Mobile",
      control: "Admin Control",
      modules: "Modules",
    },

    hero: {
      h1a: "Manage your entire workforce",
      h1b: " from a single platform",
      sub: "The all-in-one HR management system for records, attendance, payroll, leave, documents, and internal communication. Powerful web tools for your HR team, and an easy-to-use mobile app for every employee.",
      points: [
        "All-in-one HR platform",
        "Web app for management",
        "Native iOS & Android apps",
        "Light & dark modes",
      ],
      heroBrowserAlt: "Nexploy HRMS reports dashboard showing headcount and payroll analytics",
      heroPhoneAlt: "Nexploy mobile app dashboard showing attendance and leave updates",
    },

    platform: {
      eyebrow: "The Platform",
      h2: "Real screens, real features",
      p: "Take a look at the actual Nexploy interface. Explore how daily HR tasks are handled effortlessly.",
      tabs: [
        {
          label: "Employees", tag: "Employee Records",
          title: "Centralized employee profiles",
          text: "Personal, work, and financial details in one place. Keep track of documents, contracts, insurance data, and organizational structures easily.",
          points: [
            "Full employee lifecycle: hiring to resignation",
            "Departments, positions, and direct managers",
            "Manage multiple companies under one account",
            "Bulk import and export using easy templates",
          ],
          alt: "Nexploy HRMS — employee profile with personal information and employment details",
        },
        {
          label: "Attendance", tag: "Time Tracking",
          title: "Automated attendance and time tracking",
          text: "Punches sync directly from biometric devices or the mobile app. The system automatically calculates working hours, lateness, and overtime.",
          points: [
            "Daily attendance logs with instant absence alerts",
            "Overtime approvals before reaching payroll",
            "Custom work locations and flexible schedules",
            "Easy CSV export for quick reconciliation",
          ],
          alt: "Nexploy HRMS — attendance management with daily records",
        },
        {
          label: "Leave", tag: "Leave & Balances",
          title: "Self-service leave management",
          text: "Employees can view their own leave balances and submit requests instantly. Managers get notified to approve or decline on the go.",
          points: [
            "Track annual, sick, and emergency leave quotas",
            "Upload medical certificates directly",
            "Approvals with comments and full history",
            "Approved leaves sync to the company calendar",
          ],
          alt: "Nexploy HRMS — leave balance overview",
        },
        {
          label: "Messenger", tag: "Team Chat",
          title: "Built-in team communication",
          text: "Keep work conversations inside your HR platform. Direct messages, team groups, voice notes, and file sharing securely built-in.",
          points: [
            "One-to-one chats with voice notes and media",
            "Group chats with managed access",
            "Replies, reactions, and read receipts",
            "Synced seamlessly across web and mobile",
          ],
          alt: "Nexploy HRMS — internal messenger",
        },
        {
          label: "Reports", tag: "Analytics",
          title: "Smart HR reports and analytics",
          text: "Get clear answers instantly. View reports on attendance, payroll, and employee turnover. Export as PDF or Excel, or schedule them to your inbox.",
          points: [
            "Comprehensive reporting across all modules",
            "KPI cards and interactive trend charts",
            "Export data to PDF, Excel, and CSV",
            "Automated scheduled report delivery",
          ],
          alt: "Nexploy HRMS — reports analytics centre",
        },
      ],
    },

    attendance: {
      pill: "Attendance & Time",
      h2: "Time tracking that automatically flows into payroll",
      p: "Nexploy collects attendance data from fingerprint devices and the mobile app. It calculates exact working hours, lateness, undertime, and overtime automatically—no more messy spreadsheets at the end of the month.",
      points: [
        "Seamless integration with biometric devices",
        "Auto-calculation of lateness and overtime",
        "Manager approvals for overtime hours",
        "Geofenced check-ins via mobile app",
        "Quick CSV data imports and exports",
      ],
      splitPhoneAlt: "Nexploy mobile app check-in and check-out screen",
    },

    payroll: {
      pill: "Payroll",
      h2: "Error-free payroll, calculated in seconds",
      p: "Absences, late penalties, overtime, and bonuses are pulled automatically. Nexploy calculates the basic salary, deductions, taxes, and insurance to give you the final net pay ready for approval.",
      points: [
        "Automated gross and net salary calculations",
        "Customizable allowances and deductions",
        "Automatic tax and social insurance handling",
        "Salary advance installments deducted automatically",
        "Exportable payslips and bank transfer sheets",
      ],
      photoAlt: "Nexploy HRMS payroll management screen",
      splitPhoneAlt: "Nexploy mobile app salary screen",
    },


    mobile: {
      eyebrow: "Web & Mobile",
      h2: "Your HR platform, wherever your team works",
      p: "The management team gets a powerful web dashboard, while employees get a fast, native mobile app. Everything stays in sync on one database.",
      web: {
        h3: "On the Web, for HR and Management",
        p: "The complete system: manage records, run payroll, oversee attendance, configure approvals, and analyze reports with ease.",
        points: [
          "Data-dense tables built for speed",
          "Bulk operations and data imports",
          "Custom dashboards for admins",
        ],
      },
      app: {
        h3: "On Mobile, for Everyone Else",
        p: "A native app for iOS and Android. Employees can clock in, check payslips, request leaves, and chat with colleagues—all from their phones.",
        points: [
          "Check-ins, payslips, leaves, and chat",
          "Manager approvals on the go",
          "Available in English and Arabic",
        ],
      },
      screens: [
        { label: "Leave", alt: "Nexploy mobile app leave requests" },
        { label: "Messages", alt: "Nexploy mobile app messages" },
        { label: "Requests", alt: "Nexploy mobile app requests" },
        { label: "Payroll", alt: "Nexploy mobile app payroll" },
      ],
      note: "Nexploy mobile apps for iOS and Android are configured specifically for your company.",
    },

    selfService: {
      eyebrow: "Employee Self-Service",
      h2: "Less paperwork. More independence.",
      p: "Empower your employees to handle their own requests. Everything they need is just a few taps away, saving HR hours of manual work.",
      items: [
        { title: "View Personal Data", text: "Employees can check their own profile, employment details, and documents." },
        { title: "Clock In & Out", text: "Easily start and end the workday directly from the mobile app." },
        { title: "Check Payslips", text: "Access detailed salary breakdowns, deductions, and download PDF payslips." },
        { title: "Request Leave", text: "Submit time-off requests and instantly view remaining leave balances." },
        { title: "Request Documents", text: "Ask for HR letters or certificates without sending emails." },
        { title: "Salary Advances", text: "Request an advance and track the automatic repayment installments." },
        { title: "Submit Complaints", text: "Raise concerns to management and track the resolution progress." },
        { title: "Team Chat", text: "Message colleagues and stay updated through company notifications." },
      ],
    },

    automation: {
      eyebrow: "Approvals & Automation",
      h2: "Flexible approvals that fit your company",
      p: "Create custom approval workflows for every type of request. Decide who needs to approve what, and let the system handle the routing.",
      steps: [
        { title: "Request Submitted", text: "An employee submits a leave, overtime, or document request." },
        { title: "Smart Routing", text: "The request goes to the direct manager, department head, or a specific admin." },
        { title: "Conditional Steps", text: "Set rules so certain requests (like big salary advances) require higher-level approval." },
        { title: "Instant Execution", text: "Once approved, it automatically reflects in payroll or the calendar." },
      ],
      escalation: {
        pill: "Escalations",
        h3: "When issues need higher attention",
        p: "Employees can securely escalate complaints to management. Cases are tracked until fully resolved, keeping communication organized.",
        points: [
          "Direct routing to specific management groups",
          "Track status from pending to resolved",
          "In-app messaging attached to each case",
        ],
        alt: "Nexploy HRMS escalations screen",
      },
    },

    views: {
      eyebrow: "One account · three views",
      h2: "Switch between User, Manager and Admin in one click",
      p: "No separate accounts and no logging out. The same switch at the top of Nexploy changes the screen you're on, and it only offers the views your role allows.",
      hint: "Try the switch — it's the same one in the app",
      items: [
        { label: "User", title: "Your own space", text: "Your profile, attendance, payslips, leave and requests, just like every other employee.", alt: "Nexploy HRMS — User view: the employee's own profile" },
        { label: "Manager", title: "Your team at a glance", text: "Team headcount, attendance and approvals for the people who report to you.", alt: "Nexploy HRMS — Manager view dashboard" },
        { label: "Admin", title: "Admin tools, within your level", text: "Management pages open according to the admin level assigned to you, so each person sees only what they're responsible for.", alt: "Nexploy HRMS — Admin view limited by an admin level" },
      ],
    },

    control: {
      eyebrow: "Admin Levels",
      h2: "Admin levels, tailored down to one person",
      p: "Create shared levels like “Branch Manager”, or give one specific person a setup of their own. You decide every page, every button and every column they get.",
      alt: "Nexploy HRMS — admin levels: a custom permission setup for one employee with a live preview",
      steps: [
        { title: "Pick the person", text: "Choose any employee or a shared level, then start from scratch or from the access they already have." },
        { title: "Set each page", text: "Every page in the system gets one of three states:" },
        { title: "Fine-tune buttons & columns", text: "Turn actions like Export or Delete on or off, and hide sensitive columns such as Salary or National ID." },
      ],
      states: ["Hidden", "Visible · Locked", "Full access"],
      extras: [
        "Changes save instantly",
        "Live preview of what they'll see",
        "Scope by company and department",
        "Managers approve only their own team",
        "Auto-logout and secure sessions",
      ],
    },

    modules: {
      eyebrow: "Modules",
      h2: "18 powerful modules, one unified system",
      p: "Turn on only the modules you need. Everything runs smoothly on a single, unified employee database.",
      items: [
        { title: "Employee Records", text: "Manage profiles, departments, managers, and archives." },
        { title: "Attendance", text: "Track check-ins, lateness, overtime, and absences." },
        { title: "Payroll", text: "Calculate net pay, taxes, deductions, and allowances." },
        { title: "Leave Management", text: "Track balances and automate leave request approvals." },
        { title: "Documents", text: "Store company files and generate HR letters." },
        { title: "Document Requests", text: "Streamline employee requests for official paperwork." },
        { title: "Salary Advances", text: "Manage advance requests and automated installments." },
        { title: "Employee Actions", text: "Record promotions, penalties, and salary changes." },
        { title: "Workflows", text: "Build custom, multi-step approval chains." },
        { title: "Escalations", text: "Handle employee complaints and internal issues securely." },
        { title: "Messaging", text: "Internal chat with voice notes and file sharing." },
        { title: "Voice Channels", text: "Push-to-talk live audio channels for quick team communication." },
        { title: "Notifications", text: "Real-time in-app alerts for updates and approvals." },
        { title: "Company Calendar", text: "Track public holidays and approved employee leaves." },
        { title: "Reports", text: "Generate comprehensive HR and payroll analytics." },
        { title: "Tax Calculator", text: "Quickly preview income tax deductions." },
        { title: "Permissions", text: "Highly customizable access control for admins." },
        { title: "Settings", text: "Configure company policies and track all system changes." },
      ],
    },

    cta: {
      h2: "Ready to upgrade your HR?",
      p: "Book a personalized walkthrough and see how Nexploy perfectly adapts to your company's workflows.",
    },

    footer: {
      brandText: "Nexploy HRMS — The all-in-one platform for employee management, payroll, and team communication.",
      columns: {
        platform: { title: "Platform", links: ["Product Tour", "Attendance", "Payroll", "All Modules"] },
        people: { title: "For Your Team", links: ["Employee App", "Self-Service", "Approvals", "Admin Control"] },
        start: { title: "Get Started", demoLink: "Request a Demo" },
      },
      copyright: (year) => `© ${year} Nexploy — Next Generation Employees. All rights reserved.`,
    },

    modal: {
      title: "Request a Demo",
      sub: "Tell us a bit about your company, and we'll schedule a tailored demo for you.",
      name: "Full Name *", namePh: "John Doe",
      company: "Company", companyPh: "Your Company Name",
      email: "Work Email *", emailPh: "you@company.com",
      phone: "Phone Number *", phonePh: "+20 1x xxx xxxx",
      message: "Message", messagePh: "Tell us about your team size and current HR setup...",
      submit: "Send Request", sending: "Sending...",
      successTitle: "Request Sent!",
      successText: "Thanks for reaching out. Our team will contact you shortly to schedule your demo.",
      done: "Done",
      genericError: "Something went wrong. Please try again.",
      errorHint: "Or email us directly at",
      close: "Close",
    },
  },

  ar: {
    dir: "rtl",
    skipToContent: "انتقل للمحتوى",
    requestDemo: "احجز عرض تجريبي",
    signIn: "تسجيل الدخول",
    openDashboard: "افتح لوحة التحكم",
    callUs: "كلمنا على واتساب",
    phoneDisplay: "01010706163",
    menuOpen: "افتح القائمة",
    menuClose: "اقفل القائمة",
    brandHome: "Nexploy — الرئيسية",
    langSwitchLabel: "اللغة",

    nav: {
      platform: "المنصة",
      attendance: "الحضور والانصراف",
      payroll: "المرتبات",
      mobile: "الويب والموبايل",
      control: "صلاحيات الإدارة",
      modules: "الخدمات",
    },

    hero: {
      h1a: "دير شركتك كلها",
      h1b: " من سيستم واحد",
      sub: "نظام HR شامل بيريحك من اللف والدوران. بيجمع بيانات الموظفين، الحضور والانصراف، المرتبات، الإجازات، وحتى الشات الداخلي في مكان واحد. سيستم قوي على الويب للـ HR، وتطبيق موبايل سريع في جيب كل موظف.",
      points: [
        "سيستم HR متكامل",
        "ويب لإدارة الـ HR",
        "تطبيق موبايل (آيفون وأندرويد)",
        "شكل فاتح وغامق",
      ],
      heroBrowserAlt: "لوحة تحكم Nexploy للتقارير والمرتبات",
      heroPhoneAlt: "تطبيق موبايل Nexploy لخدمات الموظفين",
    },

    platform: {
      eyebrow: "المنصة",
      h2: "شوف السيستم من جوه بجد",
      p: "كل الصور دي متاخدة من جوه سيستم Nexploy الحقيقي. خد فكرة عن إزاي شغل الـ HR اليومي بيمشي بكل سهولة ومن غير تعقيد.",
      tabs: [
        {
          label: "الموظفين", tag: "ملفات الموظفين",
          title: "بروفايل واحد بيلم كل حاجة عن الموظف",
          text: "البيانات الشخصية، الورق، تواريخ العقود، التأمينات، ومين مديره المباشر.. كل ده محفوظ ومنظم في بروفايل واحد وتقدر توصله في ثانية.",
          points: [
            "دورة حياة الموظف من أول التعيين لحد الاستقالة",
            "تنظيم الأقسام والمسميات الوظيفية والمديرين",
            "إدارة أكتر من شركة تحت حساب واحد",
            "إضافة وتنزيل بيانات الموظفين بملفات إكسيل بسهولة",
          ],
          alt: "بروفايل الموظف في Nexploy",
        },
        {
          label: "الحضور", tag: "الحضور والانصراف",
          title: "حساب دقيق لساعات الشغل والتأخيرات",
          text: "السيستم بياخد البصمات من المكن أو من الموبايل، ويحسب لوحده ساعات الشغل الفعلية، التأخيرات، والغياب، والأوفر تايم كمان.",
          points: [
            "متابعة يومية للحضور والغياب والإجازات",
            "الأوفر تايم بيحتاج موافقة الأول قبل ما ينزل فلوس",
            "ربط مواقع الشغل وتحديد أماكن مرنة للموظفين",
            "تنزيل شيتات الحضور بسهولة جداً للمراجعة",
          ],
          alt: "إدارة الحضور والانصراف في Nexploy",
        },
        {
          label: "الإجازات", tag: "الإجازات والأرصدة",
          title: "إدارة الإجازات من غير وجع دماغ",
          text: "الموظف يقدر يشوف رصيد إجازاته ويطلب إجازة من الموبايل، والمدير يجيله إشعار يوافق أو يرفض، وكل ده بيسمّع في السيستم فوراً.",
          points: [
            "تتبع رصيد الإجازات السنوية، المرضية، والطارئة",
            "إمكانية رفع روشتة الدكتور في الإجازة المرضية",
            "سجل كامل لكل الموافقات والطلبات القديمة",
            "الإجازة المعتمدة بتنزل لوحدها على نتيجة الشركة",
          ],
          alt: "أرصدة الإجازات في Nexploy",
        },
        {
          label: "المحادثات", tag: "تواصل فوري",
          title: "شات مخصص للشغل جوه السيستم",
          text: "عشان تفصل بين الشغل وحياتك الخاصة.. وفرنالك شات جوه السيستم للرسايل المباشرة، الجروبات، الڤويس نوت، ومشاركة الملفات.",
          points: [
            "شات فردي بيدعم الصور، الملفات، والڤويس نوت",
            "جروبات للأقسام بصلاحيات واضحة",
            "ردود وتفاعلات زي برامج الشات اللي متعود عليها",
            "المحادثات موجودة على الويب والموبايل في نفس الوقت",
          ],
          alt: "المحادثات الداخلية في Nexploy",
        },
        {
          label: "التقارير", tag: "تحليلات ذكية",
          title: "تقارير ذكية بتوفر عليك وقت المراجعة",
          text: "بضغطة زرار، طلع تقارير عن الحضور، المرتبات، الإجازات، وغيرها. تقدر تنزلها PDF أو إكسيل، أو تظبطها تتبعتلك على الإيميل لوحدها.",
          points: [
            "تقارير شاملة لكل حاجة في السيستم",
            "رسوم بيانية ومؤشرات بتلخصلك الدنيا",
            "تصدير التقارير بصيغة PDF أو Excel أو CSV",
            "جدولة التقارير عشان توصلك أوتوماتيك",
          ],
          alt: "مركز التقارير في Nexploy",
        },
      ],
    },

    attendance: {
      pill: "الحضور والوقت",
      h2: "حضور وانصراف بيسمّع في المرتبات أوتوماتيك",
      p: "انسى شيتات الإكسيل آخر الشهر! Nexploy بيسحب البصمات من المكن أو الموبايل ويحسب ساعات الشغل والتأخير والخصومات والأوفر تايم لوحده، وبيجهزها للمرتبات.",
      points: [
        "شغال مع مكن البصمة بكل أنواعه",
        "حساب تلقائي للتأخيرات والغياب بناءً على لايحة الشركة",
        "موافقات إدارية على الأوفر تايم",
        "تسجيل الحضور من الموبايل بالـ GPS",
        "رفع وتنزيل شيتات البصمة (CSV) بسهولة",
      ],
      splitPhoneAlt: "تسجيل الحضور والانصراف من موبايل Nexploy",
    },

    payroll: {
      pill: "المرتبات",
      h2: "مرتبات بتتحسب صح، وفي ثواني",
      p: "الخصومات، الأوفر تايم، السلف، والمكافآت بتيجي لوحدها من السيستم. Nexploy بيحسب المرتب الأساسي والصافي بعد الضرائب والتأمينات عشان تعتمد المرتبات بقلب مطمن.",
      points: [
        "حساب دقيق للمرتب الإجمالي والصافي",
        "تظبيط البدلات والخصومات حسب سياسة الشركة",
        "حساب تلقائي لضرائب الدخل والتأمينات",
        "السلف بتنزل أقساطها وتتخصم أوتوماتيك",
        "تصدير قسيمة المرتب (Payslip) وشيت البنك",
      ],
      photoAlt: "إدارة المرتبات في Nexploy",
      splitPhoneAlt: "شاشة المرتب في موبايل Nexploy",
    },


    mobile: {
      eyebrow: "الويب والموبايل",
      h2: "السيستم معاك، سواء في المكتب أو بره",
      p: "إدارة الـ HR بيستخدموا شاشات الويب براحتهم، والموظفين معاهم تطبيق موبايل سريع، والاتنين بيكلموا بعض في لحظتها على نفس السيستم.",
      web: {
        h3: "على الويب، للـ HR والإدارة",
        p: "السيستم كامل بين إيديك: إدارة الملفات، تقفيل المرتبات، مراجعة الحضور، إعدادات الشركة، والتقارير الشاملة.",
        points: [
          "جداول سريعة تستحمل بيانات كتير",
          "إضافة وتعديل بيانات بشكل جماعي",
          "لوحات تحكم (Dashboards) مخصصة للإدارة",
        ],
      },
      app: {
        h3: "على الموبايل، لكل الموظفين",
        p: "تطبيق موبايل (آيفون وأندرويد) الموظف بيسجل منه حضور، يطلب إجازة، يشوف مفردات مرتبه، ويكلم زمايله في أي وقت.",
        points: [
          "حضور، إجازات، مرتبات، وشات",
          "المديرين يوافقوا على الطلبات من الموبايل",
          "التطبيق شغال عربي وإنجليزي",
        ],
      },
      screens: [
        { label: "الإجازات", alt: "الإجازات في تطبيق Nexploy" },
        { label: "الشات", alt: "الشات في تطبيق Nexploy" },
        { label: "الطلبات", alt: "الطلبات في تطبيق Nexploy" },
        { label: "الرواتب", alt: "الرواتب في تطبيق Nexploy" },
      ],
      note: "تطبيق Nexploy متصمم عشان يشتغل ويكون جاهز لشركتك مخصوص.",
    },

    selfService: {
      eyebrow: "خدمات الموظفين",
      h2: "ريّح الـ HR وادي الموظف استقلالية",
      p: "ليه الموظف يسأل الـ HR على كل حاجة؟ دلوقتي يقدر يشوف كل بياناته ويطلب اللي هو محتاجه من الموبايل بسهولة، وده بيوفر وقت ومجهود كبير.",
      items: [
        { title: "يشوف بياناته", text: "الموظف يقدر يراجع بروفايله وتفاصيل وظيفته وورقه المرفوع." },
        { title: "تسجيل حضور وانصراف", text: "يبدأ وينهي يوم شغله بضغطة زرار من الموبايل." },
        { title: "تفاصيل المرتب", text: "يشوف قسيمة الراتب (Payslip) بالتفصيل ويعرف اتخصمله إيه وليه." },
        { title: "يطلب إجازة", text: "يشوف رصيد إجازاته ويطلب إجازة وتوصل للمدير يوافق عليها." },
        { title: "يطلب مستندات", text: "يطلب HR Letter أو أي شهادة إدارية من غير ما يبعت إيميلات." },
        { title: "يطلب سلفة", text: "يطلب سلفة ويحدد هيقسطها إزاي، ويتابع الرصيد اللي فاضل عليه." },
        { title: "يرفع شكوى", text: "يقدر يوصل صوته للإدارة العليا لو فيه أي مشكلة ويتابع حلها." },
        { title: "يكلم زمايله", text: "شات داخلي بين الموظفين يفصلهم عن الواتساب وبرامج الشات العادية." },
      ],
    },

    automation: {
      eyebrow: "الموافقات الأوتوماتيك",
      h2: "دورة موافقات ماشية على نظام شركتك بالظبط",
      p: "إنت اللي بتحدد مسار كل طلب (إجازة، سلفة، أو غيره). السيستم بيودي الطلب للمدير المباشر وبعدين للي أعلى منه حسب ما إنت ظابطها.",
      steps: [
        { title: "الطلب بيتبعت", text: "الموظف بيطلب إجازة، أوفر تايم، أو سلفة من الموبايل." },
        { title: "السيستم بيوجهه صح", text: "الطلب بيروح للمدير المباشر، مدير القسم، أو للإدارة حسب الإعدادات." },
        { title: "شروط مخصصة", text: "مثلاً السلفة الكبيرة تروح لمدير الحسابات، لكن الصغيرة كفاية مدير القسم." },
        { title: "تسميع تلقائي", text: "بمجرد الموافقة، الطلب بيسمّع في المرتبات أو في نتيجة الإجازات فوراً." },
      ],
      escalation: {
        pill: "التصعيد والشكاوى",
        h3: "لما يكون فيه مشكلة محتاجة تدخل سريع",
        p: "لو فيه شكوى، الموظف يقدر يصعدها لفريق مختص، والشكوى دي بتفضل متسجلة ومفتوحة لحد ما تتابع وتتحل بالكامل.",
        points: [
          "توجيه الشكاوى لفريق الإدارة المختص",
          "متابعة حالة الشكوى (مفتوحة، قيد الحل، اتحلت)",
          "محادثة خاصة جوه الشكوى عشان الموضوع يخلص بسرعة",
        ],
        alt: "شاشة الشكاوى في Nexploy",
      },
    },

    views: {
      eyebrow: "حساب واحد · 3 شاشات",
      h2: "اتنقل بين شاشة الموظف والمدير والأدمن بضغطة واحدة",
      p: "من غير حسابات منفصلة ومن غير ما تعمل تسجيل خروج. نفس الزرار اللي فوق في Nexploy بيغيّر الشاشة اللي قدامك، وبيظهرلك بس الشاشات المسموحة لدورك.",
      hint: "جرّب الزرار، هو نفسه اللي في السيستم",
      items: [
        { label: "مستخدم", title: "مساحتك الشخصية", text: "بروفايلك وحضورك ومرتبك وإجازاتك وطلباتك، زيك زي أي موظف في الشركة.", alt: "Nexploy — شاشة المستخدم: البروفايل الشخصي للموظف" },
        { label: "مدير", title: "فريقك قدام عينك", text: "أعداد فريقك وحضورهم والموافقة على طلبات اللي تبعك.", alt: "Nexploy — لوحة شاشة المدير" },
        { label: "مسؤول", title: "أدوات الإدارة في حدود مستواك", text: "صفحات الإدارة بتفتح حسب المستوى الإداري المتحدد ليك، فكل واحد يشوف اللي هو مسؤول عنه بس.", alt: "Nexploy — شاشة المسؤول محدودة بالمستوى الإداري" },
      ],
    },

    control: {
      eyebrow: "مستويات الإدارة",
      h2: "صلاحيات إدارية مفصّلة لحد الشخص الواحد",
      p: "اعمل مستويات مشتركة زي «مدير فرع»، أو ادّي شخص بعينه إعداد مخصوص ليه لوحده. إنت اللي بتحدد كل صفحة وكل زرار وكل عمود هيشوفه.",
      alt: "Nexploy — مستويات الإدارة: إعداد صلاحيات مخصص لموظف واحد مع معاينة مباشرة",
      steps: [
        { title: "اختار الشخص", text: "اختار أي موظف أو مستوى مشترك، وابدأ من الصفر أو من الصلاحيات اللي عنده." },
        { title: "حدد كل صفحة", text: "كل صفحة في السيستم ليها حالة من 3:" },
        { title: "ظبّط الأزرار والأعمدة", text: "شغّل أو اقفل أزرار زي التصدير والحذف، واخفي أعمدة حساسة زي المرتب والرقم القومي." },
      ],
      states: ["مخفي", "ظاهر · مقفل", "وصول كامل"],
      extras: [
        "التغييرات بتتحفظ فورًا",
        "معاينة مباشرة للي هيشوفه",
        "تحديد الرؤية بالشركة والقسم",
        "المدير يوافق على طلبات فريقه بس",
        "قفل تلقائي وجلسات آمنة",
      ],
    },

    modules: {
      eyebrow: "الخدمات المتاحة",
      h2: "18 خدمة في سيستم واحد، شغل اللي تحتاجه",
      p: "تقدر تفعل الخدمات اللي شركتك محتاجاها بس، وكلهم بيقروا من نفس بيانات الموظفين ومن غير أي تعقيد.",
      items: [
        { title: "إدارة الموظفين", text: "بيانات الموظفين، الأقسام، الهيكل الإداري، والأرشيف." },
        { title: "الحضور والانصراف", text: "ساعات الشغل، التأخيرات، الأوفر تايم، ومتابعة الغياب." },
        { title: "المرتبات", text: "حساب الصافي والإجمالي، الضرائب، والتأمينات الاجتماعية." },
        { title: "إدارة الإجازات", text: "أرصدة الموظفين، دورة الموافقات، وتاريخ الإجازات." },
        { title: "المستندات", text: "حفظ ملفات الشركة والموظفين، وطباعة خطابات الـ HR." },
        { title: "طلبات المستندات", text: "تسهيل طلب الموظف لورق رسمي زي مفردات المرتب." },
        { title: "سلف الموظفين", text: "إدارة طلبات السلف وتقسيطها أوتوماتيك على المرتب." },
        { title: "إجراءات الموظفين", text: "تسجيل الترقيات، الخصومات، والزيادات في المرتب." },
        { title: "سير الموافقات", text: "عمل تسلسل موافقات مخصص لكل نوع من الطلبات." },
        { title: "الشكاوى والتصعيد", text: "تلقي شكاوى الموظفين ومتابعتها بسرية تامة لحد ما تتحل." },
        { title: "المحادثات (شات)", text: "شات داخلي آمن للموظفين بڤويس نوت ومشاركة ملفات." },
        { title: "القنوات الصوتية", text: "قنوات تواصل صوتي (لاسلكي) للفرق اللي حركتها كتير." },
        { title: "الإشعارات", text: "تنبيهات بتوصل للموبايل والويب لحظة بلحظة." },
        { title: "نتيجة الشركة", text: "تقويم مجمع للإجازات الرسمية وإجازات الموظفين المعتمدة." },
        { title: "التقارير", text: "تقارير ذكية وشاملة عن كل تفصيلة في الـ HR." },
        { title: "حاسبة الضرائب", text: "أداة سريعة لحساب ضريبة الدخل وتأثيرها على المرتب." },
        { title: "الصلاحيات", text: "إدارة دقيقة لمن يحق له رؤية وتعديل البيانات." },
        { title: "الإعدادات", text: "تظبيط سياسات الشركة ولوائح الحضور والانصراف." },
      ],
    },

    cta: {
      h2: "جاهز تنقل الـ HR في شركتك لمستوى تاني؟",
      p: "احجز عرض تجريبي دلوقتي، وهنوريك إزاي Nexploy هينظم شغلك ويمشي على نظام شركتك بالظبط.",
    },

    footer: {
      brandText: "Nexploy — سيستم متكامل لإدارة الموظفين، الحضور، المرتبات، الإجازات، والتواصل الداخلي، على الويب والموبايل.",
      columns: {
        platform: { title: "المنصة", links: ["جولة في المنتج", "الحضور والانصراف", "المرتبات", "كل الخدمات"] },
        people: { title: "لفريقك", links: ["تطبيق الموظفين", "الخدمات الذاتية", "الموافقات", "صلاحيات الإدارة"] },
        start: { title: "ابدأ معانا", demoLink: "احجز عرض تجريبي" },
      },
      copyright: (year) => `© ${year} Nexploy — الجيل الجديد لإدارة الموظفين. جميع الحقوق محفوظة.`,
    },

    modal: {
      title: "احجز عرض تجريبي",
      sub: "سيب بياناتك وقولنا كلمتين عن شركتك، وهنتواصل معاك عشان نحدد ميعاد مناسب نوريك فيه السيستم.",
      name: "الاسم بالكامل *", namePh: "اكتب اسمك",
      company: "الشركة", companyPh: "اسم شركتك",
      email: "إيميل الشغل *", emailPh: "you@company.com",
      phone: "رقم الموبايل *", phonePh: "01x xxxx xxxx",
      message: "رسالتك", messagePh: "لو حابب توضحلنا حاجة (عدد الموظفين، بتستخدموا إيه دلوقتي...)",
      submit: "ابعت الطلب", sending: "جاري الإرسال...",
      successTitle: "الطلب اتبعت بنجاح!",
      successText: "شكراً ليك.. فريقنا هيكلمك في أقرب وقت عشان نحدد ميعاد العرض التجريبي.",
      done: "تمام",
      genericError: "حصلت مشكلة، جرب تبعت تاني.",
      errorHint: "أو تقدر تبعتلنا إيميل على",
      close: "إغلاق",
    },
  },
};

export default translations;
